import { ClipboardCheck } from "lucide-react";
import React from "react";

import { cn } from "@/lib/utils";
import { ChangeAddItem } from "./ChangeAddItem";
import { ChangeItemActions } from "./ChangeItemActions";
import { PickPanel, PlacesTable } from "./PlacesTable";
import { PlacesToolbar } from "./PlacesToolbar";
import type { OzonLayout } from "./useOzonLayout";

/** Раздел «Раскладка»: чем везём, выбор, таблица мест, чек-лист площадки и примечания расчёта.
 *
 *  Состав менять нельзя — только перекладывать: количества согласованы с площадкой в заявке.
 *  Не сходится — это `problems`, и они видны сверху, а не всплывают на приёмке.
 *
 *  ⚠️ Раздел РИСУЕТ, но ничего не держит: раскладка, мутации и выбор живут в `useOzonLayout` на
 *  странице — главная кнопка стадии дёргает те же самые.
 */
export function PlacesSection({
  layout,
  transport,
  shipmentId,
  changeMode = false,
}: {
  layout: OzonLayout;
  /** Тип поставки: с грузоместами (палеты как транспортные места) или обычная. */
  transport: boolean;
  shipmentId: number;
  /** Идёт правка состава И её ещё МОЖНО править (заход в `editing`).
   *
   *  ⚠️ Не «есть запись изменения»: с первого шага каскада правки запрещены — он идёт по
   *  сохранённому плану, и переложенное место оставило бы план указывать на несуществующее.
   *  Решает это страница, а раздел только рисует. */
  changeMode?: boolean;
}): React.ReactElement {
  const { data, places, coded, edits, busy } = layout;
  // ⚠️ Раскладка не прочиталась — красный блок ВМЕСТО таблицы: пустая таблица говорила «Мест ещё
  // нет — нажмите „Разложить“» про поставку, места которой, возможно, уже стоят на площадке.
  // Кнопки, которым нужна раскладка, в это же время гасит `actionsFor` (`trouble.places`).
  if (layout.error)
    return (
      <div className="rounded-md border bg-card px-3 py-2 text-xs">
        <div className="rounded border border-red-300 bg-red-50 px-2 py-1 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          ⚠ {layout.error}
        </div>
        <p className="mt-1 text-muted-foreground">
          Пока раскладка не прочиталась, показывать места нечем — что стоит на
          площадке, экран не знает. Обновите поставку или повторите позже.
        </p>
      </div>
    );
  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs">
      <PlacesToolbar
        places={places}
        coded={coded}
        data={data}
        transport={transport}
        strategy={layout.strategy}
        setStrategy={layout.setStrategy}
        busy={busy}
        fetching={layout.fetching}
        edits={edits}
        changeMode={changeMode}
      />

      {(data?.problems.length ?? 0) > 0 && (
        <div className="mb-1.5 space-y-0.5 rounded border border-red-300 bg-red-50 px-2 py-1 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {data!.problems.map((p) => (
            <div key={p}>⚠ {p}</div>
          ))}
        </div>
      )}

      {/* ⚠️ «Добавить артикул» — единственный путь, которым состав РАСТЁТ после заявки, и живёт
          он только в режиме: вне его площадка держит согласованные количества */}
      {changeMode && (
        <div className="mb-1.5">
          <ChangeAddItem shipmentId={shipmentId} busy={busy} edits={edits} />
        </div>
      )}

      <PickPanel
        places={places}
        sel={layout.sel}
        busy={busy}
        edits={edits}
        changeMode={changeMode}
      />

      <PlacesTable
        places={places}
        transport={transport}
        fetching={layout.fetching}
        picked={layout.sel.picked}
        open={layout.open}
        togglePick={layout.togglePick}
        toggleOpen={layout.toggleOpen}
        changeMode={changeMode}
        renderItem={
          changeMode
            ? (place, item) => (
                <ChangeItemActions
                  shipmentId={shipmentId}
                  place={place}
                  item={item}
                  busy={busy}
                  stamp={layout.stamp}
                  edits={edits}
                />
              )
            : undefined
        }
      />

      {/* ⚠️ Упавший чек-лист — КРАСНОЙ строкой, а не исчезнувшим разделом: пустое место перед
          необратимой отправкой читается как «всё выполнено». «Отправить места» в это время не
          нажимается (`actionsFor`, `trouble.rules`). */}
      {layout.rulesError && (
        <div className="mt-1.5 rounded border border-red-300 bg-red-50 px-2 py-1 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          ⚠ чек-лист площадки не прочитался: {layout.rulesError}
        </div>
      )}

      {layout.rules.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-1 text-muted-foreground">
            <ClipboardCheck className="size-3.5" /> чек-лист площадки:
          </span>
          {layout.rules.map((r) => (
            <span
              key={r.rule}
              className={cn(
                r.ok
                  ? "text-emerald-600 dark:text-emerald-400"
                  : r.required
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-muted-foreground",
              )}
              title={r.detail}
            >
              {r.ok ? "✓" : r.required ? "✗" : "·"} {r.name}
              {r.detail && (
                <span className="text-muted-foreground"> ({r.detail})</span>
              )}
            </span>
          ))}
        </div>
      )}

      {(data?.notes ?? []).map((n) => (
        <div key={n} className="mt-1 text-muted-foreground">
          · {n}
        </div>
      ))}
    </div>
  );
}
